import { useCallback, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { normalizeVaultTarget, wikiLinksToMarkdown } from '@/lib/librarian/linkParser';

interface VaultPageDocumentProps {
  markdown: string;
  onNavigateInternal: (target: string) => void;
}

function isExternalHref(href: string) {
  return /^(https?:|mailto:|tel:)/i.test(href) || href.startsWith('//');
}

export default function VaultPageDocument({ markdown, onNavigateInternal }: VaultPageDocumentProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const navigateInternalTarget = useCallback((target: string) => {
    const normalizedTarget = normalizeVaultTarget(target);
    if (!normalizedTarget) {
      return;
    }

    onNavigateInternal(normalizedTarget);
  }, [onNavigateInternal]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const handleAnchorActivate = (event: Event) => {
      const anchor = event.currentTarget as HTMLAnchorElement;
      const target = anchor.dataset.vaultTarget ?? anchor.getAttribute('href') ?? '';
      if (!target) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      navigateInternalTarget(target);
    };

    const handleAnchorKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }

      handleAnchorActivate(event);
    };

    const internalAnchors = Array.from(container.querySelectorAll<HTMLAnchorElement>('a[data-vault-target]'));
    internalAnchors.forEach((anchor) => {
      anchor.style.pointerEvents = 'auto';
      anchor.style.setProperty('-webkit-app-region', 'no-drag');
      anchor.draggable = false;
      anchor.addEventListener('click', handleAnchorActivate, true);

      anchor.addEventListener('pointerup', handleAnchorActivate, true);
      anchor.addEventListener('mouseup', handleAnchorActivate, true);
      anchor.addEventListener('keydown', handleAnchorKeyDown, true);
    });

    const handleContainerActivate = (event: MouseEvent | PointerEvent) => {
      const target = event.target instanceof HTMLElement ? event.target : null;
      const internalLink = target?.closest<HTMLAnchorElement>('a[data-vault-target]');
      const linkTarget = internalLink?.dataset.vaultTarget ?? internalLink?.getAttribute('href') ?? '';
      if (!linkTarget) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      navigateInternalTarget(linkTarget);
    };

    container.addEventListener('click', handleContainerActivate, true);
    container.addEventListener('pointerup', handleContainerActivate, true);
    container.addEventListener('mouseup', handleContainerActivate, true);

    return () => {
      internalAnchors.forEach((anchor) => {
        anchor.removeEventListener('click', handleAnchorActivate, true);
        anchor.removeEventListener('pointerup', handleAnchorActivate, true);
        anchor.removeEventListener('mouseup', handleAnchorActivate, true);
        anchor.removeEventListener('keydown', handleAnchorKeyDown, true);
      });
      container.removeEventListener('click', handleContainerActivate, true);
      container.removeEventListener('pointerup', handleContainerActivate, true);
      container.removeEventListener('mouseup', handleContainerActivate, true);
    };
  }, [markdown, navigateInternalTarget]);

  return (
    <div className="rounded-2xl border bg-card shadow-sm" style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}>
      <div
        ref={containerRef}
        className="vault-page-document min-h-[22rem] px-6 py-6 text-[15px] leading-7 text-foreground sm:px-8 sm:py-8"
        style={{ WebkitAppRegion: 'no-drag' } as any}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({ node, ...props }) => <h1 className="mt-2 text-4xl font-bold tracking-tight" {...props} />,
            h2: ({ node, ...props }) => <h2 className="mt-10 text-2xl font-semibold tracking-tight text-foreground" {...props} />,
            h3: ({ node, ...props }) => <h3 className="mt-8 text-xl font-semibold text-foreground" {...props} />,
            p: ({ node, ...props }) => <p className="my-4 text-base leading-8 text-foreground/90" {...props} />,
            ul: ({ node, ...props }) => <ul className="my-4 list-disc space-y-2 pl-6" {...props} />,
            ol: ({ node, ...props }) => <ol className="my-4 list-decimal space-y-2 pl-6" {...props} />,
            li: ({ node, ...props }) => <li className="pl-1 text-foreground/90 marker:text-muted-foreground" {...props} />,
            blockquote: ({ node, ...props }) => <blockquote className="my-6 border-l-4 border-primary/30 bg-muted/40 px-4 py-3 italic" {...props} />,
            hr: ({ node, ...props }) => <hr className="my-8 border-border" {...props} />,
            code: ({ node, className, children, ...props }) => {
              const isInlineCode = !className;
              return isInlineCode ? (
                <code className="rounded-md bg-muted px-1.5 py-0.5 font-mono text-[0.9em]" {...props}>{children}</code>
              ) : (
                <code className={className} {...props}>{children}</code>
              );
            },
            pre: ({ node, ...props }) => <pre className="my-6 overflow-x-auto rounded-xl border bg-muted/60 p-4" {...props} />,
            table: ({ node, ...props }) => <table className="my-6 w-full border-collapse overflow-hidden rounded-xl border" {...props} />,
            th: ({ node, ...props }) => <th className="bg-muted/60 px-3 py-2 text-left font-semibold" {...props} />,
            td: ({ node, ...props }) => <td className="border-t px-3 py-2 align-top" {...props} />,
            a: ({ node, href, children, ...props }) => {
              const resolvedHref = href ?? '';
              const className = 'font-medium text-primary underline decoration-primary/30 underline-offset-4 transition-colors hover:text-primary/80';

              if (!resolvedHref) {
                return <span className={className}>{children}</span>;
              }

              if (isExternalHref(resolvedHref)) {
                return (
                  <a
                    href={resolvedHref}
                    className={className}
                    draggable={false}
                    style={{ WebkitAppRegion: 'no-drag', pointerEvents: 'auto' } as React.CSSProperties}
                    onClick={(event) => {
                      event.preventDefault();
                      window.open(resolvedHref, '_blank', 'noopener,noreferrer');
                    }}
                    {...props}
                  >
                    {children}
                  </a>
                );
              }

              return (
                <a
                  href={resolvedHref}
                  data-vault-target={normalizeVaultTarget(resolvedHref)}
                  className={className}
                  draggable={false}
                  style={{ WebkitAppRegion: 'no-drag', pointerEvents: 'auto' } as React.CSSProperties}
                  onPointerUp={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    navigateInternalTarget(resolvedHref);
                  }}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    navigateInternalTarget(resolvedHref);
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') {
                      return;
                    }

                    event.preventDefault();
                    event.stopPropagation();
                    navigateInternalTarget(resolvedHref);
                  }}
                  {...props}
                >
                  {children}
                </a>
              );
            },
          }}
        >
          {wikiLinksToMarkdown(markdown)}
        </ReactMarkdown>
      </div>
    </div>
  );
}